import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import Success from './Success';
import ReCAPTCHA from "react-google-recaptcha";
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
//import { RECAPTCHA_SITE_KEY } from '../../constants/constants'; // Comment this while using Google reCaptcha V3
import WpAPI from '../../WpApi';
import { useUser } from '../../context/UserContext';

// Toggle this to switch between ReCAPTCHA V2 and V3
const USE_RECAPTCHA_V3 = true;

const HelpForm = ({ option, setOption, setOpenHelpBox }) => {
    const { executeRecaptcha } = useGoogleReCaptcha();
    const ddRef = useRef();
    const captchaRef = useRef(null);
    const dispatch = useDispatch();

    const { user } = useUser();

    // State
    const [helpName, setHelpName] = useState('');
    const [helpEmail, setHelpEmail] = useState('');
    const [helpMessage, setHelpMessage] = useState('');
    const [openDD, setOpenDD] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [successResponse, setSuccessResponse] = useState(false);
    const [errorResponse, setErrorResponse] = useState(false);
    const [loginError, setLoginError] = useState(false);
    const [error, setError] = useState({});
    const [token, setToken] = useState("");

    // Fetch and set user info on mount
    useEffect(() => {
        if (user) {
            setHelpName(`${user?.firstname ?? ''} ${user?.lastname ?? ''}`.trim());
            setHelpEmail(user?.email ?? '');
        }
    }, [user?.firstname, user?.lastname, user?.email]);

    // Google reCAPTCHA v3 Handler
    const handleReCaptchaVerify = useCallback(async () => {
        if (!executeRecaptcha) {
            console.log("Execute recaptcha not yet available");
            return;
        }
        const reCaptchaToken = await executeRecaptcha("helpform");
        setToken(reCaptchaToken);
    }, [executeRecaptcha]);

    useEffect(() => {
        if (USE_RECAPTCHA_V3 && executeRecaptcha) {
            handleReCaptchaVerify();
        }
    }, [executeRecaptcha, handleReCaptchaVerify]);

    // Handle Click Outside Dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (ddRef.current && !ddRef.current.contains(event.target)) {
                setOpenDD(false);
            }
        };
        document.addEventListener('click', handleClickOutside, true);
        return () => document.removeEventListener('click', handleClickOutside, true);
    }, []);

    const handleInputChange = (setter, field) => (e) => {
        setter(e.target.value);
        setError(prev => ({ ...prev, [field]: '' }));
    };

    const validateForm = (newToken) => {
        const errors = {};
    
        if (!helpName.trim()) errors.name = "Please Enter the Name";
        if (!helpEmail.trim()) errors.email = "Please Enter the Email";
        if (!/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(helpEmail)) errors.email = "Please Enter a valid Email";
        if (!helpMessage.trim()) errors.textarea = "Please Enter the Message";
        
        // Use the latest token for validation
        if (!newToken) errors.token = "Please Check Recaptcha";
    
        setError(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {
        if (!WpAPI.isLoggedIn()) {
            setLoginError(true);
            return;
        }
    
        setProcessing(true);
    
        try {
            // Get a fresh reCAPTCHA token
            const newToken = await executeRecaptcha("helpform"); 
    
            if (!newToken) {
                throw new Error("Failed to get Recaptcha token");
            }
    
            setToken(newToken);
    
            // Directly call validateForm() with the new token
            const isValid = validateForm(newToken); 
    
            if (!isValid) {
                setProcessing(false);
                return;
            }
            
            // Proceed with form submission
            await WpAPI.submitNeedHelpData({
                subject: option,
                name: helpName,
                email: helpEmail,
                message: helpMessage
            });
    
            localStorage.removeItem("help_subject");
            setSuccessResponse(true);
        } catch (e) {
            console.error("Error during form submission:", e);
            setErrorResponse(true);
        } finally {
            setProcessing(false);
            setTimeout(() => {
                setSuccessResponse(false);
                setErrorResponse(false);
                setOption('');
                setOpenHelpBox(false);
            }, 3000);
        }
    };

    return (
        <>
            {!(successResponse || errorResponse) ? (
                <div className="helpbox helpbox2">
                    <div className="helpbox2-details">
                        <form encType="multipart/form-data" method="post" onSubmit={(e) => e.preventDefault()}>
                            <div className="help-field mb">
                                <div className="field-wrap">
                                    <label htmlFor="help_name">Name</label>
                                    <input
                                        type="text"
                                        name="help_name"
                                        onChange={handleInputChange(setHelpName, "name")}
                                        value={helpName}
                                    />
                                    {error.name && <span className="error-message">{error.name}</span>}
                                </div>
                                <div className="field-wrap">
                                    <label htmlFor="help_email">Email Address</label>
                                    <input
                                        type="text"
                                        name="help_email"
                                        onChange={handleInputChange(setHelpEmail, "email")}
                                        value={helpEmail}
                                    />
                                    {error.email && <span className="error-message">{error.email}</span>}
                                </div>
                                <div className="field-wrap">
                                    <label htmlFor="help_message">Message</label>
                                    <textarea
                                        name="help_message"
                                        id="help-message"
                                        value={helpMessage}
                                        onChange={handleInputChange(setHelpMessage, "textarea")}
                                    ></textarea>
                                    {error.textarea && <span className="error-message">{error.textarea}</span>}
                                </div>
                            </div>

                            {/* Google reCAPTCHA V2 / V3 Toggle */}
                            <div className="help-recaptcha mb">
                                {USE_RECAPTCHA_V3 ? (
                                    error.token && <span className="error-message">{error.token}</span>
                                ) : (
                                    <div className="captcha"><ReCAPTCHA sitekey={RECAPTCHA_SITE_KEY} ref={captchaRef} /></div>
                                )}
                            </div>

                            <div className="field-wrap btn-field">
                                <input
                                    type="submit"
                                    name="get_help"
                                    id="help-button"
                                    value={processing ? 'Submitting...' : 'Submit'}
                                    onClick={handleSubmit}
                                    disabled={processing}
                                />
                            </div>
                            {loginError && <p className="login-error">Please Login/Signup</p>}
                        </form>
                    </div>
                </div>
            ) : (
                <Success success={successResponse} error={errorResponse} />
            )}
        </>
    );
};

export default HelpForm;